/**
 * Cookie Consent Banner
 *
 * GDPR-compliant, user-friendly cookie consent
 * - Shows ONCE on first visit
 * - No recurring annoyance
 * - Clear, simple choices
 * - Remembers forever (until policy changes)
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Cookie as CookieIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useCookieConsent } from './useCookieConsent';

const CookieConsentBanner: React.FC = () => {
  const theme = useTheme();
  const {
    needsConsent,
    isInitialized,
    preferences,
    acceptAll,
    acceptEssentialOnly,
    saveCustomPreferences,
  } = useCookieConsent();

  const [showCustomize, setShowCustomize] = useState(false);
  const [customPrefs, setCustomPrefs] = useState({
    analytics: false,
    marketing: false,
    preferences: true,
  });

  // Don't render until after hydration (prevents pre-rendered cookie icon flash)
  if (!isInitialized || !needsConsent) {
    return null;
  }

  const handleAcceptAll = () => {
    acceptAll();
  };

  const handleAcceptEssential = () => {
    acceptEssentialOnly();
  };

  const handleSaveCustom = () => {
    saveCustomPreferences(customPrefs);
  };

  const handleToggleCustomize = () => {
    setShowCustomize(!showCustomize);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        p: 2,
        pointerEvents: 'none',
      }}
    >
      <Card
        elevation={8}
        sx={{
          maxWidth: 600,
          margin: '0 auto',
          pointerEvents: 'auto',
          background: alpha(theme.palette.background.paper, 0.98),
          backdropFilter: 'blur(10px)',
          border: `2px solid ${theme.palette.primary.main}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <CookieIcon sx={{ mr: 1.5, mt: 0.5, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Cookie Preferences
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                We use cookies to enhance your experience, analyze site usage, and personalize content.
                You can choose which cookies to accept.
              </Typography>
            </Box>
          </Box>

          {/* Essential Info */}
          <Box
            sx={{
              bgcolor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              borderRadius: 1,
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              <strong>Essential cookies</strong> are always enabled for core functionality (login, sessions, security).
              You can opt-in or out of other cookies below.
            </Typography>
          </Box>

          {/* Quick Actions */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAcceptAll}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
            >
              Accept All
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleAcceptEssential}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
            >
              Essential Only
            </Button>
            <Button
              variant="text"
              color="primary"
              onClick={handleToggleCustomize}
              endIcon={showCustomize ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}
            >
              Customize
            </Button>
          </Box>

          {/* Customize Options */}
          <Collapse in={showCustomize}>
            <Divider sx={{ mb: 2 }} />
            <FormGroup>
              {/* Essential - Always On */}
              <FormControlLabel
                control={<Switch checked disabled />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Essential Cookies (Required)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Authentication, security, core functionality
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1, alignItems: 'flex-start' }}
              />

              {/* Preferences */}
              <FormControlLabel
                control={
                  <Switch
                    checked={customPrefs.preferences}
                    onChange={(e) =>
                      setCustomPrefs({ ...customPrefs, preferences: e.target.checked })
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Preference Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Remember your settings (theme, language, etc.)
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1, alignItems: 'flex-start' }}
              />

              {/* Analytics */}
              <FormControlLabel
                control={
                  <Switch
                    checked={customPrefs.analytics}
                    onChange={(e) =>
                      setCustomPrefs({ ...customPrefs, analytics: e.target.checked })
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Analytics Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Help us understand how you use our service (Google Analytics)
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1, alignItems: 'flex-start' }}
              />

              {/* Marketing */}
              <FormControlLabel
                control={
                  <Switch
                    checked={customPrefs.marketing}
                    onChange={(e) =>
                      setCustomPrefs({ ...customPrefs, marketing: e.target.checked })
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Marketing Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Personalized content and ads
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, alignItems: 'flex-start' }}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveCustom}
                fullWidth
              >
                Save My Choices
              </Button>
            </FormGroup>
          </Collapse>

          {/* Footer Links */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Link to="/legal/cookies" style={{ textDecoration: 'none' }}>
              <Typography variant="caption" color="primary">
                Cookie Policy
              </Typography>
            </Link>
            <Typography variant="caption" color="text.secondary">
              •
            </Typography>
            <Link to="/legal/privacy" style={{ textDecoration: 'none' }}>
              <Typography variant="caption" color="primary">
                Privacy Policy
              </Typography>
            </Link>
          </Box>

          {/* One-time notice */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 1.5, fontStyle: 'italic' }}
          >
            Your choice will be remembered. You can change it anytime in Settings → Privacy.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CookieConsentBanner;
